/**
 * The auth endpoints, as the browser sees them.
 *
 * Every call returns the SERVER's view of where the sign-in stands (`status`).
 * The browser never works that out for itself — guessing at the account's state
 * from a cached client object is exactly what made the old flow loop.
 */
import type { LoginStatus } from '@agio/core/auth';
import { api } from './api.js';

export interface AuthMode {
  /** 'password' is the real thing; 'stub' is local dev (AGIO_AUTH_DISABLED=1). */
  mode: 'password' | 'stub';
  disabled: boolean;
}

let modePromise: Promise<AuthMode> | null = null;

export function getAuthMode(): Promise<AuthMode> {
  modePromise ??= api.get<AuthMode>('/auth/mode');
  return modePromise;
}

export const authApi = {
  /** Where a returning visitor stands — lets a reload resume a half-finished login. */
  session: () => api.get<{ status: LoginStatus | null }>('/auth/session'),
  login: (email: string, password: string) => api.post<{ status: LoginStatus }>('/auth/login', { email, password }),
  /** Forced (mid-login) and self-serve (signed in) both land here. */
  setPassword: (newPassword: string, currentPassword?: string) =>
    api.post<{ status: LoginStatus }>('/auth/password', { newPassword, currentPassword }),
  beginEnrollment: () => api.post<{ otpauthUri: string }>('/auth/totp/enroll', {}),
  confirmEnrollment: (code: string) => api.post<{ status: LoginStatus }>('/auth/totp/enroll/confirm', { code }),
  verifyCode: (code: string) => api.post<{ status: LoginStatus }>('/auth/totp/verify', { code }),
  logout: () => api.post<{ ok: true }>('/auth/logout', {}),
};
